import { createContext, use } from 'react';

import { type BindingResolver, emptyBindingResolver } from './bindingResolver.ts';

/**
 * React context carrying the cross-turn {@link BindingResolver} in
 * scope at the current render position. Unlike `SessionContext`, the
 * default is a real value — `emptyBindingResolver` — not `null`: a
 * component rendered outside any tool (or before any producer has run)
 * legitimately has an empty binding scope, and resolution of a free
 * name there should surface as a normal `UnboundSymbolError`, not a
 * "missing provider" crash.
 *
 * The chat plugin's tool render derives a position-scoped resolver from
 * the active message branch (`useBindingScope()`) and mounts it so the
 * tool's subtree — dashboards, materialized previews — resolves
 * `(col <name> …)` against exactly the producers visible at that point:
 *
 * ```tsx
 * <BindingScopeContext value={resolver}>
 *   <ToolBody />
 * </BindingScopeContext>
 * ```
 *
 * @see Session — the I/O memo cache the resolver memoizes through
 */
export const BindingScopeContext = createContext<BindingResolver>(emptyBindingResolver);
BindingScopeContext.displayName = 'ExprBindingScopeContext';

/**
 * Read the {@link BindingResolver} in scope at the current render
 * position. Returns `emptyBindingResolver` when no provider is mounted
 * (scope-free callers — plain expression evaluation with no cross-turn
 * references).
 */
export function useBindingResolver(): BindingResolver {
  return use(BindingScopeContext);
}
